import { useRef, useEffect, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import gsap from "gsap";

interface BuildingArrowProps {
  position: [number, number, number];
  buildingHeight: number;
  isVisible: boolean;
  color?: string;
}

/**
 * 건물 위에 표시되는 화살표 인디케이터
 * 호버 시 부드럽게 나타나고 위아래로 부유하는 애니메이션
 * 스텐실을 사용하여 GUI 삼각형 뒤에 숨김
 */
export function BuildingArrow({
  position,
  buildingHeight,
  isVisible,
  color = "#4ECDC4",
}: BuildingArrowProps) {
  const arrowRef = useRef<THREE.Group>(null);
  const meshRef = useRef<THREE.Mesh>(null);

  // 부유 애니메이션
  useFrame(({ clock }) => {
    if (arrowRef.current && isVisible) {
      const time = clock.getElapsedTime();
      const float = Math.sin(time * 2) * 0.1;
      arrowRef.current.position.y = buildingHeight + 1.5 + float;
    }
  });

  // 페이드 인/아웃
  useEffect(() => {
    if (meshRef.current) {
      gsap.to(meshRef.current.material, {
        opacity: isVisible ? 0.9 : 0,
        duration: 0.3,
        ease: "power2.out",
      });
    }
  }, [isVisible]);

  // 화살표 지오메트리 생성 (아래를 가리키는 삼각형)
  const arrowGeometry = useMemo(() => {
    const shape = new THREE.Shape();
    shape.moveTo(0, 0.1); // 위쪽 꼭짓점
    shape.lineTo(-0.15, 0); // 왼쪽 아래
    shape.lineTo(0, -0.3); // 중간 아래
    shape.lineTo(0.15, 0); // 오른쪽 아래
    shape.closePath();
    return new THREE.ShapeGeometry(shape);
  }, []);

  // 스텐실을 적용한 머티리얼 (GUI 삼각형 영역에서 숨김)
  const arrowMaterial = useMemo(() => {
    const mat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0,
      side: THREE.DoubleSide,
    });
    // 스텐실 test 적용: stencilRef=0인 영역에서만 렌더링
    mat.stencilWrite = true;
    mat.stencilFunc = THREE.EqualStencilFunc;
    mat.stencilRef = 0;
    mat.stencilFail = THREE.KeepStencilOp;
    mat.stencilZFail = THREE.KeepStencilOp;
    mat.stencilZPass = THREE.KeepStencilOp;
    return mat;
  }, [color]);

  // 색상 업데이트
  useEffect(() => {
    if (arrowMaterial) {
      arrowMaterial.color.set(color);
    }
  }, [color, arrowMaterial]);

  if (!isVisible) return null;

  return (
    <group
      ref={arrowRef}
      position={[position[0], buildingHeight + 3.0, position[2]]}
    >
      <mesh ref={meshRef} geometry={arrowGeometry} material={arrowMaterial} />
    </group>
  );
}
